import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PropertyWebsite = ({ slug, template = 'flow' }) => {
  const [property, setProperty] = useState(null);
  const [client, setClient] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchPropertyData = async () => {
      try {
        const response = await axios.get(`${API}/api/property-website/${slug}`);
        // Transform API response to match component expectations
        const apiProperty = response.data.property;
        const transformedProperty = {
          property_address: apiProperty.address,
          square_footage: apiProperty.sqft,
          client_name: response.data.client?.name,
          property_details: {
            price: apiProperty.price,
            bedrooms: apiProperty.bedrooms,
            bathrooms: apiProperty.bathrooms,
            year_built: apiProperty.year_built,
            lot_size: apiProperty.lot_size,
            description: apiProperty.description,
            features: apiProperty.features || [],
            garage: apiProperty.parking
          }
        };
        setProperty(transformedProperty);
        setClient(response.data.client);
        // Combine images and videos into media array
        const allMedia = [
          ...(response.data.images || []).map(img => ({ ...img, mime_type: img.mime_type || 'image/jpeg' })),
          ...(response.data.videos || []).map(vid => ({ ...vid, mime_type: vid.mime_type || 'video/mp4' }))
        ];
        setMedia(allMedia);
      } catch (err) {
        console.error('Error fetching property:', err);
        setError('Property not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPropertyData();
  }, [slug]);

  // Helper function to get image URL
  const getImageUrl = (mediaItem, forDownload = false) => {
    return `${API}/api/property-website/${slug}/media/${mediaItem.id}${forDownload ? '?download=true' : ''}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Property Not Found</h1>
          <p className="text-gray-600">The property you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // Choose template
  if (template === 'modern') {
    return <ModernTemplate property={property} client={client} media={media} selectedImage={selectedImage} setSelectedImage={setSelectedImage} getImageUrl={getImageUrl} />;
  }
  
  if (template === 'golden') {
    return <GoldenHourTemplate property={property} client={client} media={media} selectedImage={selectedImage} setSelectedImage={setSelectedImage} getImageUrl={getImageUrl} />;
  }
  
  return <FlowTemplate property={property} media={media} selectedImage={selectedImage} setSelectedImage={setSelectedImage} getImageUrl={getImageUrl} />;
};

// Flow Template - Clean elegant white design matching preview
const FlowTemplate = ({ property, media = [], selectedImage, setSelectedImage, getImageUrl }) => {
  const heroImage = (media || []).find(m => m.mime_type?.startsWith('image/')) || (media || [])[0];
  const images = (media || []).filter(m => m.mime_type?.startsWith('image/'));

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="font-semibold text-gray-900">Property Showcase</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('overview')} className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">Overview</button>
            <button onClick={() => scrollToSection('details')} className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">Details</button>
            {images.length > 0 && <button onClick={() => scrollToSection('gallery')} className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">Gallery</button>}
            <button onClick={() => scrollToSection('location')} className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">Location</button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-600 hover:text-blue-600 text-sm font-medium transition-colors">Contact</button>
          </div>
          <button onClick={() => scrollToSection('contact')} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-full text-sm font-medium transition-colors">
            Schedule Tour
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <div id="overview" className="pt-20">
        <div className="relative h-[75vh] overflow-hidden">
          {heroImage ? (
            <img
              src={getImageUrl(heroImage)}
              alt={property.property_address}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
              <svg className="w-32 h-32 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        </div>

        {/* Property Info Card - Overlapping Hero */}
        <div className="max-w-6xl mx-auto px-6 -mt-32 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {property.property_address}
                </h1>
                {property.property_details?.price && !isNaN(Number(property.property_details.price)) && (
                  <p className="text-3xl md:text-4xl font-bold text-blue-600">
                    ${Number(property.property_details.price).toLocaleString()}
                  </p>
                )}
              </div>
              
              {/* Quick Stats */}
              <div className="flex gap-8">
                {property.property_details?.bedrooms && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{property.property_details.bedrooms}</p>
                    <p className="text-gray-500 text-sm">Beds</p>
                  </div>
                )}
                {property.property_details?.bathrooms && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{property.property_details.bathrooms}</p>
                    <p className="text-gray-500 text-sm">Baths</p>
                  </div>
                )}
                {property.square_footage && (
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-900">{Number(property.square_footage).toLocaleString()}</p>
                    <p className="text-gray-500 text-sm">Sq Ft</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div id="details" className="max-w-6xl mx-auto px-6 py-20 scroll-mt-20">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Description */}
          {property.property_details?.description && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">About This Property</h2>
              <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-wrap">
                {property.property_details.description}
              </p>
            </div>
          )}

          {/* Property Details Grid */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Property Details</h2>
            <div className="grid grid-cols-2 gap-6">
              {property.property_details?.bedrooms && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Bedrooms</p>
                  <p className="text-xl font-semibold text-gray-900">{property.property_details.bedrooms}</p>
                </div>
              )}
              {property.property_details?.bathrooms && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Bathrooms</p>
                  <p className="text-xl font-semibold text-gray-900">{property.property_details.bathrooms}</p>
                </div>
              )}
              {property.square_footage && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Square Feet</p>
                  <p className="text-xl font-semibold text-gray-900">{Number(property.square_footage).toLocaleString()}</p>
                </div>
              )}
              {property.property_details?.year_built && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Year Built</p>
                  <p className="text-xl font-semibold text-gray-900">{property.property_details.year_built}</p>
                </div>
              )}
              {property.property_details?.lot_size && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Lot Size</p>
                  <p className="text-xl font-semibold text-gray-900">{property.property_details.lot_size}</p>
                </div>
              )}
              {property.property_details?.garage && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-500 text-sm mb-1">Garage</p>
                  <p className="text-xl font-semibold text-gray-900">{property.property_details.garage}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features */}
        {property.property_details?.features && property.property_details.features.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Features & Amenities</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {property.property_details.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-blue-50 rounded-lg px-4 py-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Photo Gallery */}
      {images.length > 0 && (
        <div id="gallery" className="bg-gray-50 py-20 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Photo Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((img, idx) => (
                <div 
                  key={img.id} 
                  className={`cursor-pointer overflow-hidden rounded-xl hover:opacity-90 transition-opacity ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                  onClick={() => setSelectedImage(img)}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Property photo ${idx + 1}`}
                    className="w-full h-full object-cover aspect-[4/3]"
                    style={idx === 0 ? { aspectRatio: '4/3' } : {}}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Map Section */}
      <div id="location" className="py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Location</h2>
          <div className="rounded-2xl overflow-hidden shadow-lg" style={{ height: '400px' }}>
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(property.property_address)}&zoom=15`}
              allowFullScreen
              title="Property Location"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="bg-blue-600 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to see this property?</h2>
          <p className="text-blue-100 text-lg mb-8">Schedule a private tour today and experience it for yourself.</p>
          {property.client_name && (
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 inline-block">
              <p className="text-white/80 text-sm mb-2">Listed by</p>
              <p className="text-white text-xl font-semibold">{property.client_name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-gray-300"
            onClick={() => setSelectedImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={getImageUrl(selectedImage, true)}
            alt="Property"
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// Dark Sleek Template - Bold contemporary dark luxury design
const ModernTemplate = ({ property, client, media = [], selectedImage, setSelectedImage, getImageUrl }) => {
  const images = (media || []).filter(m => m.mime_type?.startsWith('image/'));
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Auto-rotate hero images every 5 seconds
  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setActiveImageIndex(prev => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Sleek Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Broker/Company Logo */}
              {client?.avatar ? (
                <img src={client.avatar} alt={client.company || 'Company'} className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#0a0a0a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
              )}
              <span className="text-white font-semibold text-lg tracking-wide">{client?.company?.toUpperCase() || 'LUXURY ESTATES'}</span>
            </div>
            <div className="hidden md:flex items-center gap-10">
              <button onClick={() => scrollToSection('hero')} className="text-white/70 hover:text-white text-sm tracking-widest transition-colors">HOME</button>
              <button onClick={() => scrollToSection('details')} className="text-white/70 hover:text-white text-sm tracking-widest transition-colors">DETAILS</button>
              {images.length > 0 && <button onClick={() => scrollToSection('gallery')} className="text-white/70 hover:text-white text-sm tracking-widest transition-colors">GALLERY</button>}
              <button onClick={() => scrollToSection('contact')} className="text-white/70 hover:text-white text-sm tracking-widest transition-colors">CONTACT</button>
            </div>
            <button onClick={() => scrollToSection('contact')} className="bg-white text-[#0a0a0a] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors">
              INQUIRE NOW
            </button>
          </div>
        </div>
      </nav>

      {/* Full-screen Hero with Auto-rotating Slider */}
      <div id="hero" className="relative h-screen">
        {images.length > 0 ? (
          <>
            <div className="absolute inset-0">
              <img
                src={getImageUrl(images[activeImageIndex])}
                alt={property.property_address}
                className="w-full h-full object-cover transition-opacity duration-1000"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
            
            {/* Slide Indicators */}
            {images.length > 1 && (
              <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-2">
                {images.slice(0, 6).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`h-1 rounded-full transition-all duration-300 ${activeImageIndex === idx ? 'bg-white w-12' : 'bg-white/30 w-8 hover:bg-white/50'}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
            <svg className="w-32 h-32 text-white/10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
        )}
        
        {/* Property Info Overlay */}
        <div className="absolute left-0 top-0 bottom-0 w-full md:w-3/5 flex flex-col justify-center px-8 md:px-20">
          <div className="space-y-8">
            {property.property_details?.price && !isNaN(Number(property.property_details.price)) && (
              <p className="text-6xl md:text-8xl font-bold tracking-tight">
                ${Number(property.property_details.price).toLocaleString()}
              </p>
            )}
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              {property.property_address}
            </h1>
            
            {/* Property Stats Bar */}
            <div className="flex flex-wrap items-center gap-8 pt-4">
              {property.property_details?.bedrooms && (
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold">{property.property_details.bedrooms}</span>
                  <span className="text-white/60 text-sm uppercase tracking-wider">Beds</span>
                </div>
              )}
              {property.property_details?.bathrooms && (
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold">{property.property_details.bathrooms}</span>
                  <span className="text-white/60 text-sm uppercase tracking-wider">Baths</span>
                </div>
              )}
              {property.square_footage && (
                <div className="flex items-center gap-3">
                  <span className="text-4xl font-bold">{Number(property.square_footage).toLocaleString()}</span>
                  <span className="text-white/60 text-sm uppercase tracking-wider">Sq Ft</span>
                </div>
              )}
            </div>

            <button onClick={() => scrollToSection('contact')} className="inline-flex items-center gap-3 bg-white text-[#0a0a0a] px-8 py-4 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors mt-4">
              SCHEDULE A VIEWING
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div id="details" className="bg-[#111111] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-8 py-24">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Description */}
            {property.property_details?.description && (
              <div>
                <h2 className="text-sm uppercase tracking-[0.3em] text-white/50 mb-6">About</h2>
                <p className="text-xl text-white/80 leading-relaxed whitespace-pre-wrap">
                  {property.property_details.description}
                </p>
              </div>
            )}

            {/* Property Details Grid */}
            <div>
              <h2 className="text-sm uppercase tracking-[0.3em] text-white/50 mb-6">Property Details</h2>
              <div className="grid grid-cols-2 gap-6">
                {property.property_details?.bedrooms && (
                  <div className="bg-white/5 rounded-2xl p-6">
                    <p className="text-4xl font-bold mb-2">{property.property_details.bedrooms}</p>
                    <p className="text-white/50 text-sm uppercase tracking-wider">Bedrooms</p>
                  </div>
                )}
                {property.property_details?.bathrooms && (
                  <div className="bg-white/5 rounded-2xl p-6">
                    <p className="text-4xl font-bold mb-2">{property.property_details.bathrooms}</p>
                    <p className="text-white/50 text-sm uppercase tracking-wider">Bathrooms</p>
                  </div>
                )}
                {property.square_footage && (
                  <div className="bg-white/5 rounded-2xl p-6">
                    <p className="text-4xl font-bold mb-2">{Number(property.square_footage).toLocaleString()}</p>
                    <p className="text-white/50 text-sm uppercase tracking-wider">Sq Feet</p>
                  </div>
                )}
                {property.property_details?.year_built && (
                  <div className="bg-white/5 rounded-2xl p-6">
                    <p className="text-4xl font-bold mb-2">{property.property_details.year_built}</p>
                    <p className="text-white/50 text-sm uppercase tracking-wider">Year Built</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Features */}
          {property.property_details?.features && property.property_details.features.length > 0 && (
            <div className="mt-20">
              <h2 className="text-sm uppercase tracking-[0.3em] text-white/50 mb-8">Features & Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {property.property_details.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/5 rounded-xl px-5 py-4">
                    <div className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                    <span className="text-white/80">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gallery Section */}
      {images.length > 0 && (
        <div id="gallery" className="bg-[#0a0a0a] py-24 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-8">
            <h2 className="text-sm uppercase tracking-[0.3em] text-white/50 mb-12">Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((img, idx) => (
                <div 
                  key={img.id} 
                  className={`cursor-pointer overflow-hidden rounded-2xl ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                  onClick={() => setSelectedImage(img)}
                >
                  <img
                    src={getImageUrl(img)}
                    alt={`Property photo ${idx + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 aspect-[4/3]"
                    style={idx === 0 ? { aspectRatio: '4/3' } : {}}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contact Section with Agent Card */}
      <div id="contact" className="bg-[#111111] py-24 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-8">
          <h2 className="text-sm uppercase tracking-[0.3em] text-white/50 mb-12 text-center">Contact</h2>
          
          {/* Agent Card */}
          <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-3xl p-10 mb-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Agent Avatar */}
              {client?.avatar ? (
                <img src={client.avatar} alt={client.name} className="w-28 h-28 rounded-2xl object-cover" />
              ) : (
                <div className="w-28 h-28 rounded-2xl bg-white flex items-center justify-center text-4xl font-bold text-[#0a0a0a]">
                  {client?.name?.charAt(0)?.toUpperCase() || 'A'}
                </div>
              )}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold text-white mb-1">{client?.name || 'Contact Agent'}</h3>
                {client?.company && <p className="text-white/50 mb-4">{client.company}</p>}
                <div className="flex flex-col md:flex-row gap-4">
                  {client?.email && (
                    <a href={`mailto:${client.email}`} className="flex items-center justify-center md:justify-start gap-2 text-white/70 hover:text-white transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{client.email}</span>
                    </a>
                  )}
                  {client?.phone && (
                    <a href={`tel:${client.phone}`} className="flex items-center justify-center md:justify-start gap-2 text-white/70 hover:text-white transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{client.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="rounded-3xl overflow-hidden" style={{ height: '350px' }}>
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(property.property_address)}&zoom=15`}
              allowFullScreen
              title="Property Location"
            ></iframe>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-8 right-8 text-white hover:text-gray-300"
            onClick={() => setSelectedImage(null)}
          >
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={getImageUrl(selectedImage, true)}
            alt="Property"
            className="max-w-full max-h-screen object-contain p-4"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// Golden Hour Template - Warm sunset-inspired luxury design
const GoldenHourTemplate = ({ property, client, media = [], selectedImage, setSelectedImage, getImageUrl }) => {
  const images = (media || []).filter(m => m.mime_type?.startsWith('image/'));
  const [currentSlide, setCurrentSlide] = useState(0);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Magazine Editorial Color Palette - Warm neutrals with terracotta accent
  const colors = {
    cream: '#FAF7F2',
    sand: '#E8E0D5', 
    charcoal: '#2C2C2C',
    warmGray: '#6B6560',
    terracotta: '#C4704F',
    terracottaDark: '#A85A3A',
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream, fontFamily: "'Georgia', serif" }}>
      
      {/* Minimal Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 mix-blend-difference">
        <div className="flex justify-between items-center px-8 md:px-16 py-6">
          <span className="text-white text-sm tracking-[0.3em] uppercase font-light">
            {client?.company || 'Realty'}
          </span>
          <button 
            onClick={() => scrollToSection('contact')}
            className="text-white text-sm tracking-[0.2em] uppercase font-light hover:opacity-70 transition-opacity"
          >
            Inquire
          </button>
        </div>
      </nav>

      {/* HERO - Editorial Opening Spread */}
      <section id="hero" className="min-h-screen relative">
        {/* Large Hero Image - Offset Position */}
        <div className="absolute top-0 right-0 w-full md:w-3/4 h-[85vh]">
          {images.length > 0 ? (
            <img
              src={getImageUrl(images[0])}
              alt={property.property_address}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full" style={{ backgroundColor: colors.sand }} />
          )}
        </div>
        
        {/* Editorial Title Block - Offset Left */}
        <div className="relative z-10 pt-32 md:pt-48 px-8 md:px-16">
          <div className="max-w-xl">
            {/* Issue/Category Tag */}
            <p className="text-xs tracking-[0.4em] uppercase mb-8" style={{ color: colors.terracotta }}>
              Featured Property
            </p>
            
            {/* Large Display Price */}
            {property.property_details?.price && !isNaN(Number(property.property_details.price)) && (
              <p className="text-7xl md:text-9xl font-light tracking-tight leading-none mb-6" style={{ color: colors.charcoal }}>
                ${(Number(property.property_details.price) / 1000000).toFixed(1)}M
              </p>
            )}
            
            {/* Address as Headline */}
            <h1 className="text-3xl md:text-4xl font-light leading-tight mb-12" style={{ color: colors.charcoal }}>
              {property.property_address}
            </h1>
            
            {/* Stats in Editorial Style */}
            <div className="flex gap-12 text-sm" style={{ color: colors.warmGray }}>
              {property.property_details?.bedrooms && (
                <div>
                  <span className="text-4xl font-light block" style={{ color: colors.charcoal }}>{property.property_details.bedrooms}</span>
                  <span className="tracking-[0.2em] uppercase text-xs">Bedrooms</span>
                </div>
              )}
              {property.property_details?.bathrooms && (
                <div>
                  <span className="text-4xl font-light block" style={{ color: colors.charcoal }}>{property.property_details.bathrooms}</span>
                  <span className="tracking-[0.2em] uppercase text-xs">Baths</span>
                </div>
              )}
              {property.square_footage && (
                <div>
                  <span className="text-4xl font-light block" style={{ color: colors.charcoal }}>{Number(property.square_footage).toLocaleString()}</span>
                  <span className="tracking-[0.2em] uppercase text-xs">Sq Ft</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-8 md:left-16">
          <div className="flex items-center gap-4" style={{ color: colors.warmGray }}>
            <div className="w-12 h-px" style={{ backgroundColor: colors.warmGray }} />
            <span className="text-xs tracking-[0.3em] uppercase">Scroll</span>
          </div>
        </div>
      </section>

      {/* EDITORIAL DESCRIPTION SPREAD */}
      <section id="details" className="py-32 px-8 md:px-16" style={{ backgroundColor: colors.cream }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-8 md:gap-16">
            {/* Pull Quote - Large Typography */}
            <div className="md:col-span-5 md:col-start-1">
              <p className="text-4xl md:text-5xl font-light leading-snug italic" style={{ color: colors.charcoal }}>
                "Where modern elegance meets timeless design"
              </p>
              <div className="w-24 h-px mt-8" style={{ backgroundColor: colors.terracotta }} />
            </div>
            
            {/* Description Text */}
            <div className="md:col-span-5 md:col-start-7">
              {property.property_details?.description && (
                <p className="text-lg leading-relaxed whitespace-pre-wrap" style={{ color: colors.warmGray }}>
                  {property.property_details.description}
                </p>
              )}
              
              {/* Features as Inline List */}
              {property.property_details?.features && property.property_details.features.length > 0 && (
                <div className="mt-12 pt-8 border-t" style={{ borderColor: colors.sand }}>
                  <p className="text-xs tracking-[0.3em] uppercase mb-6" style={{ color: colors.terracotta }}>Amenities</p>
                  <p className="text-base leading-loose" style={{ color: colors.warmGray }}>
                    {property.property_details.features.join(' · ')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ASYMMETRIC GALLERY GRID */}
      {images.length > 0 && (
        <section id="gallery" className="py-24" style={{ backgroundColor: colors.sand }}>
          <div className="px-8 md:px-16 mb-16">
            <p className="text-xs tracking-[0.4em] uppercase" style={{ color: colors.terracotta }}>Gallery</p>
          </div>
          
          {/* Editorial Grid - Asymmetric Layout */}
          <div className="px-4 md:px-8">
            <div className="grid grid-cols-12 gap-4">
              {/* Large Feature Image */}
              {images[0] && (
                <div 
                  className="col-span-12 md:col-span-8 aspect-[16/10] cursor-pointer overflow-hidden"
                  onClick={() => setSelectedImage(images[0])}
                >
                  <img
                    src={getImageUrl(images[0])}
                    alt="Property feature"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              )}
              
              {/* Stacked Right Images */}
              <div className="col-span-12 md:col-span-4 flex flex-col gap-4">
                {images[1] && (
                  <div 
                    className="aspect-[4/3] cursor-pointer overflow-hidden"
                    onClick={() => setSelectedImage(images[1])}
                  >
                    <img
                      src={getImageUrl(images[1])}
                      alt="Property detail"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
                {images[2] && (
                  <div 
                    className="aspect-[4/3] cursor-pointer overflow-hidden"
                    onClick={() => setSelectedImage(images[2])}
                  >
                    <img
                      src={getImageUrl(images[2])}
                      alt="Property detail"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
              </div>

              {/* Bottom Row - Offset */}
              {images[3] && (
                <div 
                  className="col-span-6 md:col-span-5 md:col-start-2 aspect-[4/3] cursor-pointer overflow-hidden"
                  onClick={() => setSelectedImage(images[3])}
                >
                  <img
                    src={getImageUrl(images[3])}
                    alt="Property detail"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              )}
              {images[4] && (
                <div 
                  className="col-span-6 md:col-span-5 aspect-[4/3] cursor-pointer overflow-hidden"
                  onClick={() => setSelectedImage(images[4])}
                >
                  <img
                    src={getImageUrl(images[4])}
                    alt="Property detail"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              )}
            </div>

            {/* Additional Images in Horizontal Scroll */}
            {images.length > 5 && (
              <div className="mt-8 flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {images.slice(5).map((img, idx) => (
                  <div 
                    key={img.id}
                    className="flex-shrink-0 w-72 aspect-[4/3] cursor-pointer overflow-hidden"
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={getImageUrl(img)}
                      alt={`Property photo ${idx + 6}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* PROPERTY DETAILS - Magazine Specs Layout */}
      <section className="py-32 px-8 md:px-16" style={{ backgroundColor: colors.cream }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-16">
            {/* Section Label */}
            <div className="md:col-span-3">
              <p className="text-xs tracking-[0.4em] uppercase sticky top-32" style={{ color: colors.terracotta }}>
                Property Details
              </p>
            </div>
            
            {/* Details Grid */}
            <div className="md:col-span-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-16 gap-y-12">
                {property.property_details?.bedrooms && (
                  <div className="border-l-2 pl-6" style={{ borderColor: colors.terracotta }}>
                    <span className="text-5xl font-light" style={{ color: colors.charcoal }}>{property.property_details.bedrooms}</span>
                    <p className="text-sm tracking-[0.15em] uppercase mt-2" style={{ color: colors.warmGray }}>Bedrooms</p>
                  </div>
                )}
                {property.property_details?.bathrooms && (
                  <div className="border-l-2 pl-6" style={{ borderColor: colors.terracotta }}>
                    <span className="text-5xl font-light" style={{ color: colors.charcoal }}>{property.property_details.bathrooms}</span>
                    <p className="text-sm tracking-[0.15em] uppercase mt-2" style={{ color: colors.warmGray }}>Bathrooms</p>
                  </div>
                )}
                {property.square_footage && (
                  <div className="border-l-2 pl-6" style={{ borderColor: colors.terracotta }}>
                    <span className="text-5xl font-light" style={{ color: colors.charcoal }}>{Number(property.square_footage).toLocaleString()}</span>
                    <p className="text-sm tracking-[0.15em] uppercase mt-2" style={{ color: colors.warmGray }}>Square Feet</p>
                  </div>
                )}
                {property.property_details?.year_built && (
                  <div className="border-l-2 pl-6" style={{ borderColor: colors.terracotta }}>
                    <span className="text-5xl font-light" style={{ color: colors.charcoal }}>{property.property_details.year_built}</span>
                    <p className="text-sm tracking-[0.15em] uppercase mt-2" style={{ color: colors.warmGray }}>Year Built</p>
                  </div>
                )}
                {property.property_details?.lot_size && (
                  <div className="border-l-2 pl-6" style={{ borderColor: colors.terracotta }}>
                    <span className="text-5xl font-light" style={{ color: colors.charcoal }}>{property.property_details.lot_size}</span>
                    <p className="text-sm tracking-[0.15em] uppercase mt-2" style={{ color: colors.warmGray }}>Lot Size</p>
                  </div>
                )}
                {property.property_details?.garage && (
                  <div className="border-l-2 pl-6" style={{ borderColor: colors.terracotta }}>
                    <span className="text-5xl font-light" style={{ color: colors.charcoal }}>{property.property_details.garage}</span>
                    <p className="text-sm tracking-[0.15em] uppercase mt-2" style={{ color: colors.warmGray }}>Garage</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION - Full Width Map with Editorial Overlay */}
      <section id="location" className="relative">
        <div className="h-[70vh]">
          <iframe
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0, filter: 'grayscale(100%) contrast(1.1)' }}
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(property.property_address)}&zoom=14`}
            allowFullScreen
            title="Property Location"
          ></iframe>
        </div>
        {/* Location Label Overlay */}
        <div className="absolute bottom-0 left-0 right-0 py-8 px-8 md:px-16" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }}>
          <p className="text-white text-lg font-light">{property.property_address}</p>
        </div>
      </section>

      {/* CONTACT - Editorial Closing */}
      <section id="contact" className="py-32 px-8 md:px-16" style={{ backgroundColor: colors.charcoal }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-12 gap-16 items-center">
            {/* Agent Info */}
            <div className="md:col-span-7">
              <p className="text-xs tracking-[0.4em] uppercase mb-8" style={{ color: colors.terracotta }}>
                Represented By
              </p>
              <h3 className="text-4xl md:text-5xl font-light text-white mb-4">
                {client?.name || 'Contact Agent'}
              </h3>
              {client?.company && (
                <p className="text-xl font-light mb-8" style={{ color: colors.warmGray }}>
                  {client.company}
                </p>
              )}
              
              <div className="space-y-4">
                {client?.email && (
                  <a 
                    href={`mailto:${client.email}`} 
                    className="block text-lg hover:opacity-70 transition-opacity"
                    style={{ color: colors.sand }}
                  >
                    {client.email}
                  </a>
                )}
                {client?.phone && (
                  <a 
                    href={`tel:${client.phone}`} 
                    className="block text-lg hover:opacity-70 transition-opacity"
                    style={{ color: colors.sand }}
                  >
                    {client.phone}
                  </a>
                )}
              </div>
              
              <button 
                className="mt-12 px-12 py-4 text-sm tracking-[0.2em] uppercase transition-all hover:opacity-80"
                style={{ backgroundColor: colors.terracotta, color: 'white' }}
              >
                Schedule Viewing
              </button>
            </div>
            
            {/* Agent Photo */}
            <div className="md:col-span-5 md:col-start-9">
              {client?.avatar ? (
                <img 
                  src={client.avatar} 
                  alt={client.name} 
                  className="w-full aspect-[3/4] object-cover"
                  style={{ filter: 'grayscale(30%)' }}
                />
              ) : (
                <div 
                  className="w-full aspect-[3/4] flex items-center justify-center text-6xl font-light"
                  style={{ backgroundColor: colors.warmGray, color: colors.cream }}
                >
                  {client?.name?.charAt(0)?.toUpperCase() || 'A'}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-8 md:px-16 border-t" style={{ backgroundColor: colors.charcoal, borderColor: colors.warmGray }}>
        <div className="flex justify-between items-center">
          <span className="text-xs tracking-[0.3em] uppercase" style={{ color: colors.warmGray }}>
            {client?.company || 'Luxury Realty'}
          </span>
          <span className="text-xs" style={{ color: colors.warmGray }}>
            © {new Date().getFullYear()}
          </span>
        </div>
      </footer>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(44,44,44,0.98)' }}
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-8 right-8 text-white hover:opacity-70 transition-opacity"
            onClick={() => setSelectedImage(null)}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={getImageUrl(selectedImage, true)}
            alt="Property"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PropertyWebsite;
